"use client";

import React from "react";
import { Shield, Search, Scan, ArrowRight, CircleCheck as CheckCircle2, FileCheck, Wrench, GraduationCap, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const operationalCapabilities = [{
  id: "op-1",
  title: "Field Narcotics Screening",
  badge: "Tactical Identification",
  description: "Rapid, non-destructive identification of unknown substances, synthetic opioids, cutting agents, and illicit compounds directly at the point of intercept.",
  detail: "Handheld Raman and NIR analyzers with library matching, timing application dependent.",
  iconName: "Search"
}, {
  id: "op-2",
  title: "Law Enforcement Operations",
  badge: "Evidentiary Integrity",
  description: "Rugged analytical equipment built for crime scene investigations, forensic triage, and high-risk field containment scenarios.",
  detail: "Sealed enclosures, tamper-evident data logging, and court-admissible reporting.",
  iconName: "Shield"
}, {
  id: "op-3",
  title: "Border Security & Customs",
  badge: "Checkpoint Screening",
  description: "High-throughput inspection protocols for ports of entry, cargo checkpoints, and international parcel processing facilities.",
  detail: "Through-barrier scanning of clear and translucent packaging materials, with opaque-material capability technology dependent.",
  iconName: "Scan"
}];
const linkedSolutions = [{
  id: "sol-1",
  title: "Law Enforcement & Public Safety",
  description: "Specialized handheld detection tools and protocols for tactical frontline units.",
  path: "/LawEnforcementPublicSafetyPage"
}, {
  id: "sol-2",
  title: "Border Security and Defence",
  description: "Inspection systems designed for customs agencies, defense forces, and border gates.",
  path: "/BorderSecurityAndDefencePage"
}, {
  id: "sol-3",
  title: "Safety and Security Sector",
  description: "Comprehensive analytical instrumentation for threat assessment and hazardous materials.",
  path: "/SafetyAndSecurityPage"
}];
const antechServices = [{
  id: "srv-1",
  name: "Application Consultation & Method Setup",
  description: "Expert guidance on instrument selection and protocol alignment.",
  path: "/ApplicationConsultationPage"
}, {
  id: "srv-2",
  name: "Feasibility Studies & Library Creation",
  description: "Sample testing and custom spectral reference library builds.",
  path: "/FeasibilityStudiesPage"
}, {
  id: "srv-3",
  name: "Qualification & Lifecycle Services",
  description: "Installation, operational qualification, and routine verification.",
  path: "/QualificationLifecycleServicesPage"
}, {
  id: "srv-4",
  name: "Preventive Maintenance & Repairs",
  description: "Routine servicing, rapid diagnostics, and certified recalibration.",
  path: "/PreventiveMaintenanceAndRepairsPage"
}, {
  id: "srv-5",
  name: "Operator Training & Application Support",
  description: "Hands-on instruction for field staff and laboratory analysts.",
  path: "/TrainingApplicationSupportPage"
}];
export default function OperationsSplit() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (name: string) => {
    switch (name) {
      case "Search":
        return <Search className="size-5 text-primary" />;
      case "Shield":
        return <Shield className="size-5 text-primary" />;
      case "Scan":
        return <Scan className="size-5 text-primary" />;
      default:
        return <Sparkles className="size-5 text-primary" />;
    }
  };
  return <section id="operationsandservicessplit" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 border-b border-border">
      <div className="page-container mx-auto">
        {/* Section Header */}
        <div className="max-w-3xl mb-14 lg:mb-20">
          <Badge variant="secondary" className="mb-3 font-medium">
            Operational Capabilities & Support
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground mb-4">
            Field Interception & Lifecycle Services
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Direct deployment tools for security personnel alongside end-to-end technical support, training, and qualification from Antech Solutions across Ireland and the UK.
          </p>
        </div>

        {/* 6/6 Split Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left Column (6 Cols): Field Operations Focus */}
          <div className="lg:col-span-6 flex flex-col gap-6">
            <div className="pb-2">
              <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground block mb-1">
                Field Deployment
              </span>
              <h3 className="text-xl sm:text-2xl font-default font-semibold text-foreground">
                Tactical Operations & Interdiction
              </h3>
            </div>

            <div className="flex flex-col gap-5">
              {operationalCapabilities.map((item, i) => <motion.div key={item.id} data-index={i} initial={shouldReduceMotion ? {
              opacity: 1
            } : {
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: "-50px"
            }} transition={{
              duration: 0.4,
              delay: i * 0.1
            }}>
                  <Card className="border border-border bg-card shadow-xs hover:shadow-md transition-shadow">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <div className="size-10 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                          {getIcon(item.iconName)}
                        </div>
                        <Badge variant="outline" className="text-xs font-normal text-muted-foreground">
                          {item.badge}
                        </Badge>
                      </div>
                      <CardTitle className="text-lg font-default font-semibold text-card-foreground">
                        {item.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                        {item.description}
                      </CardDescription>
                      <div className="p-3 rounded-md bg-secondary/60 border border-border/50 text-xs text-foreground font-sans">
                        <span className="font-semibold text-primary block mb-0.5">Deployment Spec:</span>
                        {item.detail}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>)}
            </div>
          </div>

          {/* Right Column (6 Cols): Linked Solutions & Antech Services */}
          <div className="lg:col-span-6 flex flex-col gap-8">
            {/* Sector Portals */}
            <div>
              <div className="pb-2 mb-4">
                <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground block mb-1">
                  Target Sectors
                </span>
                <h3 className="text-xl sm:text-2xl font-default font-semibold text-foreground">
                  Sector Solutions
                </h3>
              </div>

              <div className="grid grid-cols-1 gap-3">
                {linkedSolutions.map((solution, i) => <motion.div key={solution.id} data-index={i} initial={shouldReduceMotion ? {
                opacity: 1
              } : {
                opacity: 0,
                y: 16
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                margin: "-50px"
              }} transition={{
                duration: 0.4,
                delay: i * 0.1
              }}>
                    <Link to={solution.path} className="group block p-4 rounded-xl border border-border bg-card hover:border-primary/50 hover:bg-secondary/40 transition-all">
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <h4 className="text-base font-default font-semibold text-card-foreground group-hover:text-primary transition-colors flex items-center gap-2">
                            {solution.title}
                          </h4>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {solution.description}
                          </p>
                        </div>
                        <div className="size-8 rounded-md bg-secondary flex items-center justify-center shrink-0 text-muted-foreground group-hover:text-primary group-hover:bg-primary/10 transition-colors">
                          <ArrowRight className="size-4" />
                        </div>
                      </div>
                    </Link>
                  </motion.div>)}
              </div>
            </div>

            <Separator className="my-2" />

            {/* Antech Support Services List */}
            <div>
              <div className="flex items-center justify-between gap-4 mb-4">
                <div>
                  <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground block mb-1">
                    Antech Support
                  </span>
                  <h3 className="text-lg sm:text-xl font-default font-semibold text-foreground">
                    Lifecycle & Technical Services
                  </h3>
                </div>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/ServicesPage" className="inline-flex items-center gap-1.5">
                    <span>All Services</span>
                    <ArrowRight className="size-3.5" />
                  </Link>
                </Button>
              </div>

              <div className="rounded-xl border border-border bg-card p-5 space-y-4">
                <p className="text-xs sm:text-sm text-muted-foreground">
                  Our application specialists support analytical systems through every stage of adoption and compliance.
                </p>

                <div className="space-y-3">
                  {antechServices.map((service, i) => <div key={service.id} data-index={i} className="flex items-start gap-3 text-xs sm:text-sm">
                      <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <Link to={service.path} className="font-medium text-foreground hover:text-primary hover:underline transition-colors block">
                          {service.name}
                        </Link>
                        <span className="text-xs text-muted-foreground block mt-0.5">
                          {service.description}
                        </span>
                      </div>
                    </div>)}
                </div>

                <div className="pt-2 border-t border-border flex flex-wrap items-center justify-between gap-3">
                  <span className="text-xs text-muted-foreground">
                    Need immediate technical verification or a feasibility trial?
                  </span>
                  <Button variant="default" size="sm" asChild>
                    <Link to="/DiscussYourApplicationPage">
                      <span>Discuss Application</span>
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}